import { LongLayoverIcon, ShortLayoverIcon } from '@/src/assets/svgs/LayoverIcons'
import { PerAdultLabel } from '@/src/components/common/srp/perAdultLabel'
import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { useFormatTime } from '@/src/hooks/srp/formatTime'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { TranslationFunction } from '@/src/types/translations'
import { getAirlineIcons } from '@/src/utils/confirmation/airlineUtils'
import { getFormattedDate } from '@/src/utils/date'
import { replacePlaceHolderString } from '@/src/utils/generalUtils/replacePlaceholder'
import type { MulticityFlightData, MulticityJourney } from '@/src/utils/srp/flightResultsUtil'
import CircleIcon from '@mui/icons-material/Circle'
import { Box, Button, CardContent, Divider, Tooltip, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import React, { useMemo } from 'react'

interface MulticityFlightCardProps {
    data: MulticityFlightData
    onBook: () => void
    onViewDetails?: () => void
    tCommon: TranslationFunction<'common'>
    tFlightDetails: TranslationFunction<'srpFlightDetails'>
    isBookingLoading?: boolean
    isDisabled?: boolean
    buttonText?: string
}

const NbAirportBadge: React.FC<{ airportCode: string }> = ({ airportCode }) => {
    const tCommon = useTranslations('common')
    const t = useTranslations('srpFlightDetails')
    const { jsons } = useSearchResults()
    const airportName = useMemo(() => jsons?.ap?.[airportCode?.toUpperCase()]?.n || '', [airportCode, jsons])
    return (
        <Tooltip
            title={
                <Box>
                    <Typography fontWeight={600} color="inherit" fontSize="12px">
                        {tCommon('nearbyAirport')}
                    </Typography>
                    <Typography fontWeight={500} color="inherit" fontSize="10px">
                        {airportCode} - {airportName}
                    </Typography>
                    <Typography color="inherit" sx={{ mt: 0.5 }} fontWeight={400} fontSize="10px">
                        {t('nearbyAirportCta')}
                    </Typography>
                </Box>
            }
            placement="top"
        >
            <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: 'primary.main', bgcolor: 'primary.A100', px: 1, borderRadius: 1, display: 'inline-block', cursor: 'help' }}
            >
                {airportCode}
            </Typography>
        </Tooltip>
    )
}

const JourneyRow: React.FC<{
    journey: MulticityJourney
    tCommon: TranslationFunction<'common'>
}> = ({ journey, tCommon }) => {
    const formatTime = useFormatTime()
    const { formatDate } = useFormatDate()
    const t = useTranslations('srpFlightDetails')

    const {
        airlineName,
        airlineCodes,
        departureTime,
        arrivalTime,
        departureAirport,
        arrivalAirport,
        departureDate,
        arrivalDate,
        stops,
        via,
        durationSeconds,
        layovers,
        sectorIndex,
        isNbDeparture,
        isNbArrival
    } = journey

    const duration = formatTime(durationSeconds)
    const isNextDayCallOut = !!departureDate && !!arrivalDate && departureDate !== arrivalDate

    const getLayoverType = (layoverMinutes: number) =>
        layoverMinutes > 240 ? 'longLayover' : 'shortLayover'

    return (
        <Box display="flex" alignItems="center" gap={1.5}>
            {/* Journey label */}
            <Box width="84px" flexShrink={0}>
                <Typography fontSize="11px" fontWeight={700} color="primary.main">
                    {tCommon('journey', { number: sectorIndex + 1 })}
                </Typography>
                {departureDate && (
                    <Typography fontSize="11px" color="grey.600">
                        {formatDate(getFormattedDate(departureDate, 'MM/DD/YYYY'), 'D MMM')}
                    </Typography>
                )}
            </Box>

            {/* Airline */}
            <Box
                display="flex"
                alignItems="center"
                flexDirection="column"
                width="96px"
                sx={{ wordBreak: 'break-word' }}
            >
                <Box display="flex" maxWidth={140} overflow="auto" gap={0.5}>
                    {getAirlineIcons(airlineCodes, 28, 28)}
                </Box>
                <Typography textAlign="center" variant="body2" color="grey.600" fontSize="11px">
                    {airlineName}
                </Typography>
            </Box>

            {/* Times + route */}
            <Box display="flex" alignItems="flex-end" gap={1} flex={1} justifyContent={"space-between"}>
                <Box display="flex" alignItems="center" flexDirection="column" width="56px">
                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: 'black' }}>
                        {departureTime}
                    </Typography>
                    {isNbDeparture ? (
                        <NbAirportBadge airportCode={departureAirport} />
                    ) : (
                        <Typography variant="body2" fontWeight={600} color="grey.600">
                            {departureAirport}
                        </Typography>
                    )}
                </Box>

                <Box display="flex" alignItems="center" flexDirection="column">
                    <Typography fontSize="12px" color="grey.600">
                        {duration}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mx: 1 }}>
                        <CircleIcon sx={{ fontSize: 6, color: 'grey.600' }} />
                        <Box height="1px" width="120px" bgcolor="grey.600" />
                        <CircleIcon sx={{ fontSize: 6, color: 'grey.600' }} />
                    </Box>
                    <Box display="flex" alignItems="center">
                        <Typography
                            fontSize="12px"
                            color={stops === 0 ? '#16a34a' : 'grey.600'}
                            fontWeight={stops === 0 ? 600 : 400}
                        >
                            {stops === 0
                                ? tCommon('direct')
                                : `${stops} ${stops > 1 ? tCommon('stops', { s: 's' }) : tCommon('stops', { s: '' })}`}
                        </Typography>
                        {via && (
                            <>
                                <CircleIcon sx={{ fontSize: 5, color: 'grey.600', mx: 0.5 }} />
                                <Typography component="span" sx={{ fontSize: '12px', color: 'textColors.primary' }}>
                                    {via}
                                </Typography>
                            </>
                        )}
                        {Array.isArray(layovers) && layovers.length > 0 && (() => {
                            const layover = layovers[0]
                            const type = getLayoverType(layover.durationMins)
                            const LayoverIcon = type === 'longLayover' ? LongLayoverIcon : ShortLayoverIcon
                            return (
                                <Tooltip
                                    title={
                                        <Box>
                                            <Typography fontWeight={600} fontSize="12px">
                                                {t(type)}
                                            </Typography>
                                            <Typography fontWeight={400} fontSize="10px">
                                                {t('layover')}: {layover.durationStr} in {layover.city}
                                            </Typography>
                                        </Box>
                                    }
                                    placement="top"
                                >
                                    <Box display="inline-flex" alignItems="center" sx={{ cursor: 'help', ml: 1 }}>
                                        <LayoverIcon />
                                    </Box>
                                </Tooltip>
                            )
                        })()}
                    </Box>
                </Box>

                <Box display="flex">
                    <Box display="flex" alignItems="center" flexDirection="column" width="56px">
                        <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: 'black' }}>
                            {arrivalTime}
                        </Typography>
                        {isNbArrival ? (
                            <NbAirportBadge airportCode={arrivalAirport} />
                        ) : (
                            <Typography variant="body2" fontWeight={600} color="grey.600">
                                {arrivalAirport}
                            </Typography>
                        )}
                    </Box>
                    {isNextDayCallOut && (
                        <Box minWidth="40px" px={0.5}>
                            <Typography
                                variant="body2"
                                fontSize={12}
                                px={1}
                                py={0.3}
                                fontWeight={600}
                                color="primary.main"
                                bgcolor="primary.A100"
                                borderRadius={1}
                            >
                                {formatDate(getFormattedDate(arrivalDate, 'MM/DD/YYYY'), 'D MMM')}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

const MulticityFlightCard: React.FC<MulticityFlightCardProps> = ({
    data,
    onBook,
    onViewDetails,
    tCommon,
    tFlightDetails,
    isBookingLoading = false,
    isDisabled = false,
    buttonText
}) => {
    const { formatCurrency } = useFormatCurrencyForSrp()
    const { isPerPaxPricingEnabled } = useSearchResults()
    const { journeys, price, strikeOffPrice, seatsLeft, coupon_detail } = data

    const couponDetails = coupon_detail?.discountAmount
        ? {
            ...coupon_detail,
            discountedPrice: formatCurrency(coupon_detail.discountedPrice),
            discountAmount: formatCurrency(coupon_detail.discountAmount)
        }
        : {}

    return (
        <Box sx={{ p: 0, bgcolor: 'white' }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Box display="flex" alignItems="stretch" justifyContent="space-between" gap={2}>
                    {/* Left: stacked journeys */}
                    <Box flex={1} display="flex" flexDirection="column" gap={1.5}>
                        {journeys.map((journey, idx) => (
                            <React.Fragment key={journey.sectorIndex}>
                                {idx > 0 && <Divider flexItem sx={{ borderStyle: 'dashed' }} />}
                                <JourneyRow journey={journey} tCommon={tCommon} />
                            </React.Fragment>
                        ))}
                    </Box>

                    {/* Right: combined price + actions */}
                    <Box
                        textAlign="right"
                        flexShrink={0}
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="flex-end"
                        sx={{ borderLeft: '1px solid #eee', pl: 2, minWidth: 170 }}
                    >
                        {typeof seatsLeft === 'number' && seatsLeft > 0 && seatsLeft <= 5 && (
                            <Box
                                component="span"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    bgcolor: '#fff0f0',
                                    color: '#c62828',
                                    borderRadius: 1,
                                    fontSize: 12,
                                    fontWeight: 500,
                                    px: 1.2,
                                    py: 0.2,
                                    mb: 0.5
                                }}
                            >
                                {seatsLeft}{' '}
                                {seatsLeft > 1
                                    ? tFlightDetails('seatLeft', { s: 's' })
                                    : tFlightDetails('seatLeft', { s: '' })}
                            </Box>
                        )}
                        <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: 'black', lineHeight: 1.2 }}>
                            {formatCurrency(Number(price))} <PerAdultLabel />
                        </Typography>
                        {typeof strikeOffPrice === 'number' && strikeOffPrice > Number(price) && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'grey.500',
                                    textDecoration: 'line-through',
                                    fontWeight: 400,
                                    fontSize: '0.75rem',
                                    mb: 0.5
                                }}
                            >
                                {formatCurrency(strikeOffPrice)}
                            </Typography>
                        )}
                        {coupon_detail?.discountAmount && coupon_detail?.discountAmount > 0 && (
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.75rem',
                                    color: '#16a34a',
                                    fontWeight: 500
                                }}
                            >
                                {replacePlaceHolderString(
                                    isPerPaxPricingEnabled
                                        ? coupon_detail?.adultCouponMessage
                                        : coupon_detail?.message,
                                    couponDetails
                                )}
                            </Typography>
                        )}
                        <Button
                            variant="customVariantPrimary"
                            onClick={() => onBook()}
                            disabled={isBookingLoading || isDisabled}
                            sx={{
                                mt: 1,
                                px: '1rem',
                                py: '0.25rem',
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                textTransform: 'none',
                                minWidth: 100,
                                height: 40
                            }}
                        >
                            {isBookingLoading ? tCommon('Loading') : buttonText || tCommon('bookNow')}
                        </Button>
                        {onViewDetails && (
                            <Button
                                variant="text"
                                onClick={() => onViewDetails()}
                                sx={{
                                    color: 'apple.blue',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    p: 0,
                                    mt: 0.5,
                                    minWidth: 'auto',
                                    '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' }
                                }}
                            >
                                {tCommon('flightDetails')} →
                            </Button>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Box>
    )
}

export { MulticityFlightCard }
export type { MulticityFlightCardProps }
export default MulticityFlightCard
